// Notes on a renewal (spec C7.4, S14): reading a renewal's notes, the toast after a note is added
// with its Undo, and Add note for the lists and the Quick view (the popup, then the toast).
import { useEffect, useState } from 'react';
import type { Api, NotesView, RenewalRow } from '../auth/api';
import { Toast } from '../shell/Toast';
import { AddNoteDialog } from './AddNoteDialog';
import { takeBackNote, type NoteUndo } from './notes-lines';

type Toasted = { id: number; text: string; undo: NoteUndo | null };

/** The toast after a note is added, with Undo, and after the Undo. */
export function useNoteToast(api: Api, onChanged: () => void) {
  const [toast, setToast] = useState<Toasted | null>(null);
  const show = (text: string, undo: NoteUndo | null) =>
    setToast((last) => ({ id: (last?.id ?? 0) + 1, text, undo }));
  const undo = async (u: NoteUndo) => {
    show(await takeBackNote(api, u), null);
    onChanged();
  };
  const element = toast && (
    <Toast
      key={toast.id}
      text={toast.text}
      action={toast.undo ? { label: 'Undo', onClick: () => void undo(toast.undo!) } : undefined}
      shownMs={toast.undo ? 8000 : undefined}
    />
  );
  return { show, element };
}

/** Add note for the lists and the Quick view: the popup, then the toast with Undo. */
export function useAddNote(api: Api, onChanged: () => void) {
  const [row, setRow] = useState<RenewalRow | null>(null);
  const toast = useNoteToast(api, onChanged);
  const element = (
    <>
      {row && (
        <AddNoteDialog
          api={api}
          row={row}
          onClose={() => setRow(null)}
          onAdded={(text, undo) => {
            setRow(null);
            toast.show(text, undo);
            onChanged();
          }}
        />
      )}
      {toast.element}
    </>
  );
  return { open: (r: RenewalRow) => setRow(r), element };
}

/** The renewal's notes, read again whenever `version` changes. null while loading. */
export function useNotes(api: Api, renewalId: string, version: number) {
  const [view, setView] = useState<NotesView | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    api.notes(renewalId).then(
      (v) => {
        if (!live) return;
        setView(v);
        setFailed(false);
      },
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [api, renewalId, version]);
  return { view, failed };
}
